import React, {
  useRef,
  useEffect,
  useState,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";
import SearchPanel from "../../../../../widgets/SearchPanel";
import { fnGetDataSet, fnSave } from "@shared/api/restApi";
import { getInitFormData } from "@shared/utils/CommonUtils";
import { showDialog } from "@widgets/CommonDialog";
import { global } from "@shared/libs/Global";
import SnGrid from "@shared/components/Grid/SnGrid";
import { menuInfo } from "@shared/type/CommonType";
import SharedForm from "../../../../../widgets/SharedForm";
import SnResizable from "@shared/components/SnResizable";
import usePanelSizes from "@shared/hooks/usePanelSizes";

interface Props {
  Menuprops: menuInfo;
}

interface RowData {
  KeyField?: string;

  [key: string]: any;
}

/**
 * 변수 속성(ID/Type/Default/Format)은 sys_formctrl의 SnVarProps 컨트롤이 담당한다.
 * cd_param을 js_param으로 잡아두면 SharedForm이 그 컬럼에 JSON 배열을 실어주므로,
 * 저장은 기존 grw_form 한 덩어리로 그대로 나간다(saveData에 키를 추가할 필요 없음).
 * grw_form 컬럼 구성: 본문 HTML = js_body, 변수 속성 = js_param.
 */

/** 양식코드가 담기는 컬럼. 상세폼 컨트롤의 cd_param과 같은 이름이어야 한다. */
const FORM_CODE_PARAM = "no_form";

/**
 * 양식코드는 PK라 한 번 저장하면 바꿀 수 없다.
 * 신규로 추가한 행에서만 입력되고, 조회된 행을 클릭했을 때는 비활성화되도록
 * 상세폼 컨트롤에 편집조건을 심어준다.
 *
 * SharedForm이 dc_editcon 안의 'new'를 행 상태로 바꿔치기한 뒤 평가하는데
 * (SharedForm.tsx:259-277), 신규면 false·기존 행이면 true가 되고
 * 그 결과가 그대로 읽기전용 여부로 쓰인다(:315-328).
 * 즉 조건이 'new' 하나면 "신규일 때만 입력 가능"이 된다.
 *
 * 'new'는 replace(첫 1건만)로 치환되므로 조건 안에 한 번만 넣어야 한다.
 */
const lockFormCodeOnUpdate = (controls: any[]) =>
  (controls ?? []).map((control) => {
    if (control?.cd_param !== FORM_CODE_PARAM) return control;
    const prev = (control.dc_editcon ?? "").trim();
    return { ...control, dc_editcon: prev ? `new || (${prev})` : "new" };
  });

// 양식등록
const SN_GRW_BAS_REG_002 = forwardRef(({ Menuprops }: Props, ref) => {
  //#region Variable
  const { userInfo } = global();
  const searchRef = useRef<any>(null);
  const gridref1 = useRef<any>(null);
  const SharedFormref1 = useRef<any>();
  //#endregion

  //#region Components useState
  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns, setColums] = useState<any[]>([]);
  const [dataSource1, setDataSource1] = useState<any>();
  const [sharedForm, setSharedForm] = useState<any[]>([]);
  const [focusedRowData, setFocusedRowData] = useState<any>();
  const [gridFocus, setGridFocus] = useState(false);
  //#endregion

  //#region panel 사이즈 관련
  const cd_menu = Menuprops.cd_menu || "SN_GRW_BAS_REG_002";
  const defaultSize = { panel1width: "23.5%", panel2width: "76.5%" };
  const [sizes, onResizeStop] = usePanelSizes(cd_menu, defaultSize);
  //#endregion

  useImperativeHandle(ref, () => ({
    onSearch,
    onInsert,
    onDelete,
    onSave,
  }));

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();

        setFormData(rtn.formData);
        setFormControlData(rtn.formControlData);
        setColums(rtn.grid.grid1);
        setSharedForm(lockFormCodeOnUpdate(rtn.form.freeform1));
      } catch (error) {}
    };
    fetchform();
  }, []);

  useEffect(() => {
    gridref1.current.selectAndFocusRow(0);
    setGridFocus(false);
  }, [gridFocus]);

  //region onSearch
  const onSearch = async () => {
    try {
      const modifiedRowsH = gridref1.current.getChange();
      if (modifiedRowsH.modifiedRowcount > 0) {
        const confirm = await showDialog(
          "confirm",
          "수정된 내용이 있습니다. 저장하시겠습니까?"
        );
        if (confirm) {
          const result: any = await onSave();
          if (!result) return;
        } else {
          gridref1.current.resetUpdate();
        }
      }

      const params = {
        id: "sn_grw_bas_reg_002_01",
        params: searchRef.current.getFormdata(),
      };

      setFocusedRowData(null);
      setDataSource1([]);
      const data: any = await fnGetDataSet(params);
      setDataSource1(data[0]);
      if (data[0] && data[0].length > 0) {
        setGridFocus(true);
      }
    } catch (error) {}
  };

  //region onInsert
  const onInsert = async () => {
    try {
      const newRow = {};
      if (gridref1.current) gridref1.current.addRow(newRow);
    } catch (e) {}
  };

  //region onBeforeAddRowH
  const onBeforeAddRowH = (newRow: RowData): boolean => {
    try {
      const updatedRow = {
        cd_firm: userInfo.cd_firm,
        yn_use: "Y",
        // 이미 추가 중인 행들 뒤에 붙도록 순번을 1씩 증가시킨다
        no_seq: (gridref1.current.getChange().insert?.length ?? 0) + 1,
      };
      Object.assign(newRow, updatedRow); // 원본 객체 수정

      return true;
    } catch (error: any) {
      return false;
    }
  };

  //region onDelete
  const onDelete = async () => {
    try {
      gridref1.current.deleteSelectedRows();

      const rowCount = gridref1.current?.api.getDisplayedRowCount();
      if (!rowCount) setFocusedRowData(null);
    } catch (error) {}
  };

  //region onSave
  const onSave = async () => {
    try {
      const requiredValuesCheck = gridref1.current.requiredValuesCheck();
      if (!requiredValuesCheck) {
        return;
      }

      const gridGetChanges = gridref1.current.getChange();
      if (gridGetChanges.modifiedRowcount == 0) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return;
      }

      if (gridGetChanges.update) {
        gridGetChanges.update = gridGetChanges.update.map((row: any) => ({
          ...row,
          cd_user_amd: userInfo.cd_user,
        }));
      }

      const saveData = {
        grw_form: gridGetChanges,
      };
      const result = await fnSave("SN_GRW_BAS_REG_002_SAVE_01", saveData);
      gridref1.current.resetUpdate(!result);
      return result;
    } catch (error: any) {
      showDialog("error", error.message);
    }
  };

  //region onFocusedRowChanged
  const onFocusedRowChanged = useCallback(
    (focusedRowData: any | null) => {
      setFocusedRowData(focusedRowData);
    },
    [focusedRowData]
  );
  //#endregion

  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}

        ></SearchPanel>
      </div>
      <div className="w-full flex h-full min-h-0 space-x-1.5">
        <SnResizable
          direction={"width"}
          seq={1}
          cd_menu={cd_menu}
          defaultSize={defaultSize}
          size={sizes.panel1width}
          onResizeStop={onResizeStop}
          enable={{ right: true }}
          minWidth={130}
        >
          <SnGrid

            seq={1}
            ref={gridref1}
            enableAddRow={true}
            columnConfig={columns} // 컬럼 설정 전달
            rowData={dataSource1}
            enableExternalChart={false}
            isEditable={false}
            rowSelection="single" // 단일 선택 모드
            onFocusedRowChanged={onFocusedRowChanged}
            onBeforeAddRow={onBeforeAddRowH}
            cellSelection={false}
            title="양식등록"
            resizeController={{
              defaultSize: defaultSize,
              onResizeStop: onResizeStop,
            }}
          />
        </SnResizable>
        <SnResizable
          direction={"width"}
          seq={2}
          cd_menu={cd_menu}
          defaultSize={defaultSize}
          size={sizes.panel2width}
          onResizeStop={onResizeStop}
          enable={{ right: true }}
          minWidth={130}
        >
          <SharedForm
            ref={SharedFormref1}
            data={focusedRowData}
            controls={sharedForm}
            gridref={gridref1}
            title="상세정보"
          ></SharedForm>
        </SnResizable>
      </div>
    </div>
  );
});

export default SN_GRW_BAS_REG_002;
